import { useCallback, useState } from "react";
import { Unit } from "types/landlord/address";
import { CurrentTenantsSection } from "views/properties-id/components/CurrentTenantsSection";
import { ListingSection } from "./ListingSection";
import { GeneralSection } from "./GeneralSection";
import { ListingAppearanceSection } from "./ListingAppearanceSection";
import { LockboxSection } from "./LockboxSection";
import {
  ENTRY_INSTRUCTIONS_CARD_ID,
  InstructionsSection
} from "./InstructionsSection";
import { PlanSection } from "./PlanSection";
import { ConstructionTypeSection } from "./ConstructionTypeSection";
import { AmenitiesSection } from "./AmenitiesSection";
import { MarketingInfoSection } from "./MarketingInfoSection";
import { PhotosSection } from "./PhotosSection";
import { Show } from "shared/components";
import { RentalInfoSection } from "shared/app/RentalInfoSection";
import { useAuthContext } from "providers/Auth";
import { revealElement } from "../reveal-element";

interface UnitDetailsProps {
  unit?: Unit;
}

const revealEntryInstructions = () => revealElement(ENTRY_INSTRUCTIONS_CARD_ID);

export const UnitDetails = ({ unit }: UnitDetailsProps) => {
  const { type } = useAuthContext();
  const [entryEditRequest, setEntryEditRequest] = useState(0);
  const requestEntryEdit = useCallback(
    () => setEntryEditRequest((count) => count + 1),
    []
  );

  if (!unit) return null;

  const isSelfGuided =
    unit.property.prescreening_template?.showing_type === "self_guided";

  return (
    <div>
      <div className="space-y-4 max-w-[822px] w-full">
        <ListingSection
          propertyKind={unit.property.kind}
          propertyId={unit.property.id}
          unit={unit}
        />
        <Show if={type.isBoomCRM}>
          <ListingAppearanceSection unit={unit} />
        </Show>
        <GeneralSection unit={unit} />
        <Show if={isSelfGuided}>
          <LockboxSection
            unit={unit}
            propertyId={unit.property.id}
            propertyTimezone={unit.property.timezone}
            onAddEntryInstructions={requestEntryEdit}
            onShowEntryInstructions={revealEntryInstructions}
          />
          <div
            id={ENTRY_INSTRUCTIONS_CARD_ID}
            className="rounded-lg data-[flash]:animate-[list-unit-flash_1.5s_ease-out]"
          >
            <InstructionsSection
              unit={unit}
              type="entry"
              editRequest={entryEditRequest}
            />
          </div>
          <InstructionsSection unit={unit} type="exit" />
        </Show>
        <RentalInfoSection unit={unit} />
        <Show if={unit.property.kind || type.isPmsListingSyncEnabled}>
          <PlanSection unit={unit} />
        </Show>
        <ConstructionTypeSection unit={unit} />
        <CurrentTenantsSection
          propertyId={unit.property.id}
          unitId={unit.id}
          initialTenants={unit.tenant_contacts}
        />
        <Show if={unit.property.kind || type.isPmsListingSyncEnabled}>
          <AmenitiesSection unit={unit} />
          <MarketingInfoSection unit={unit} />
          <PhotosSection unit={unit} />
        </Show>
      </div>
    </div>
  );
};
